import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_NUMERIC_TEXT_INPUT,
  OPTIONAL_QUANTITY_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "./optionalInputs";

export const warehouseFreightDefaultInputSchema = z.object({
  dataSourceLink: z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid().optional(),
  }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),
  customerNumber: OPTIONAL_NUMERIC_TEXT_INPUT.describe(
    "Customer number of the source row. Only a branch's own customer number (a branch customer account, kept by upsert_warehouse_customer_account) yields a default; other customers are skipped.",
  ),
  shipViaCode: OPTIONAL_TEXT_INPUT,
  freightTermsCode: OPTIONAL_TEXT_INPUT,
  transportMode: OPTIONAL_TEXT_INPUT,
  minShippingWeight: OPTIONAL_QUANTITY_INPUT,
});
export type WarehouseFreightDefaultInput = z.infer<
  typeof warehouseFreightDefaultInputSchema
>;

export const upsertWarehouseFreightDefaultStep = defineStepConfig({
  type: "upsert_warehouse_freight_default",
  name: "Upsert Warehouse Freight Default",
  description:
    "Maintains each receiving branch's default carrier, freight terms and transport mode from the branch's customer record, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: warehouseFreightDefaultInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "not_a_branch",
        "missing_division",
        "ambiguous_branch",
        "stale_event",
        "not_found",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    writeOriginSystem: z.string().min(1),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
